import clsx from 'clsx'

import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    IconButton,
    LinearProgress,
    makeStyles,
    Divider,
    Typography
} from '@material-ui/core'
import { CSSProperties } from '@material-ui/styles'

import CloseIcon from '@material-ui/icons/Close'

type StyleProps = {
    closeButtonRight: number
    closeButtonTop: number
}

const useStyles = makeStyles(theme => ({
    transparent: {
        backgroundColor: 'transparent',
        boxShadow: 'none'
    },
    title: {
        textAlign: 'center',
        fontWeight: theme.fontWeightBold
    },
    noPadding: {
        padding: 0
    },
    small: {
        fontSize: theme.spacing(2)
    },
    medium: {
        fontSize: theme.spacing(3)
    },
    large: {
        fontSize: theme.spacing(4)
    },
    closeButton: {
        position: 'absolute',
        right: ({ closeButtonRight }: StyleProps) => closeButtonRight,
        top: ({ closeButtonTop }: StyleProps) => closeButtonTop,
        padding: theme.spacing(0.5),
        margin: theme.spacing(0.5),
        zIndex: 15
    },
    code: {
        padding: theme.spacing(1),
        border: '1px solid gainsboro',
        borderRadius: '4px'
    },
    actions: {
        padding: theme.spacing(3),
        justifyContent: 'center'
    },
    darkBackdrop: {
        backgroundColor: 'black',
        opacity: '0.85 !important'
    }
}))

type Props = {
    maxWidth?: DialogProps['maxWidth']
    isOpen: boolean
    onClose: () => void
    onConfirm?: () => void
    loading?: boolean
    title?: string | null
    titleSize?: 'sm' | 'md' | 'lg'
    content: JSX.Element
    confirmBtnText?: string
    closeBtnText?: string
    extraActions?: JSX.Element
    hideConfirmBtn?: boolean
    hideCloseBtn?: boolean
    showDivider?: boolean
    disabled?: boolean
    justifyActions?: CSSProperties['justifyContent']
    noPadding?: boolean
    noBackground?: boolean
    darkBackdrop?: boolean
    closeButtonRight?: number
    closeButtonTop?: number
}

export const CommonDialog = ({
    maxWidth = 'sm',
    isOpen,
    onClose,
    onConfirm,
    loading = false,
    title,
    titleSize = 'sm',
    content,
    closeBtnText = 'Close',
    confirmBtnText = 'Confirm',
    justifyActions = 'center',
    extraActions,
    hideConfirmBtn = false,
    hideCloseBtn = false,
    showDivider = false,
    disabled = false,
    noPadding,
    noBackground,
    darkBackdrop,
    closeButtonRight = 0,
    closeButtonTop = 0
}: Props) => {
    const classes = useStyles({ closeButtonRight, closeButtonTop })

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth
            maxWidth={maxWidth}
            BackdropProps={{
                className: darkBackdrop ? classes.darkBackdrop : ''
            }}
            classes={{
                paper: noBackground ? classes.transparent : ''
            }}
        >
            <>
                {loading && <LinearProgress />}
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>

                {title && (
                    <DialogTitle>
                        <Typography
                            className={clsx(classes.title, {
                                [classes.small]: titleSize === 'sm',
                                [classes.medium]: titleSize === 'md',
                                [classes.large]: titleSize === 'lg'
                            })}
                        >
                            {title}
                        </Typography>
                    </DialogTitle>
                )}

                {showDivider && <Divider />}

                <DialogContent
                    className={clsx({
                        [classes.noPadding]: noPadding
                    })}
                >
                    {content}
                </DialogContent>

                {(!hideCloseBtn || !hideConfirmBtn || extraActions) && (
                    <DialogActions
                        className={classes.actions}
                        style={{
                            justifyContent: justifyActions
                        }}
                    >
                        {extraActions}
                        {!hideCloseBtn && (
                            <Button size="medium" color="default" onClick={onClose}>
                                {closeBtnText}
                            </Button>
                        )}
                        {!hideConfirmBtn && (
                            <Button
                                size="medium"
                                onClick={onConfirm}
                                disabled={loading || disabled}
                            >
                                {confirmBtnText}
                            </Button>
                        )}
                    </DialogActions>
                )}
            </>
        </Dialog>
    )
}
